/* eslint-disable react/display-name */
import clsx from 'clsx'
import ReactInputMask from 'react-input-mask'
import { useState } from 'react'
import {
  Controller,
  type FieldArrayWithId,
  useFormContext,
  type UseFieldArrayUpdate,
} from 'react-hook-form'

import { type ClientDomain } from '../../../../../../../types/domain/client-domain'
import { type ClientContactsFormValues } from './index'
import { contactObjectSchema } from '../../../../../../../validators/clientSchema'
import { Input } from '../../../../../../../components/FormElements/Input'
import { Button } from '../../../../../../../components/Button'
import { ReactComponent as Add } from '../../../../../../../assets/svgs/add.svg'
import { ReactComponent as Edit } from '../../../../../../../assets/svgs/edit.svg'
import { ReactComponent as Trash } from '../../../../../../../assets/svgs/secondary-trash.svg'

type ClientContactFormProps = {
  isSubmitting: boolean
  defaultValues?: ClientDomain
  contactFields: Array<
  FieldArrayWithId<ClientContactsFormValues, 'contacts', 'id'>
  >
  onAddNewContact: () => void
  onSubmit: (data: ClientContactsFormValues) => void
  onRemoveContact: (id: number) => void
  onUpdateContact: UseFieldArrayUpdate<ClientContactsFormValues, 'contacts'>
}

export const ClientContactForm = ({
  defaultValues,
  contactFields,
  isSubmitting,
  onSubmit,
  onAddNewContact,
  onRemoveContact,
  onUpdateContact,
}: ClientContactFormProps) => {
  const [currentContactIdBeingEdited, setCurrentContactIdBeingEdited] =
    useState<number | null>(null)
  const { control, formState, handleSubmit, getValues, watch } =
    useFormContext<ClientContactsFormValues>()

  const handleSaveContact = (index: number) => {
    onUpdateContact(index, getValues('contacts')[index])
    setCurrentContactIdBeingEdited(null)
  }

  const contactsWatch = watch('contacts')
  const isContactFieldsEmpty =
  contactsWatch?.[0] &&
    Object.entries(contactsWatch[0])
      .filter(([key]) => ['name', 'email'].includes(key))
      .every(([_, value]) => !value)

  const canAddNewContact =
  contactsWatch &&
    contactObjectSchema.isValidSync(contactsWatch[0]) &&
    !isContactFieldsEmpty

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="mb-[28px]">
        <Controller
          name="name"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { invalid, error },
          }) => (
            <Input
              label="Cliente"
              placeholder="Cliente"
              value={value}
              onChange={onChange}
              isValid={!invalid && !!error}
              errorMessage={error?.message}
            />
          )}
        />
      </fieldset>

      {contactFields?.length <= 1 ? (
        <fieldset className="mb-14">
          <div className="mb-4">
            <Controller
              name="contacts.0.name"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <Input
                  label="Nome do contato"
                  placeholder="Nome do contato"
                  value={value}
                  onChange={onChange}
                  isValid={!invalid && !!error}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>

          <div className="mb-4">
            <Controller
              name="contacts.0.email"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <Input
                  label="E-mail"
                  placeholder="E-mail"
                  value={value}
                  onChange={onChange}
                  isValid={!invalid && !!error}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>

          <div className="mb-6">
            <Controller
              name="contacts.0.phone"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <ReactInputMask
                  mask="(99) 99999-9999"
                  maskPlaceholder={null}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    label="Telefone"
                    placeholder="(00) 00000-0000"
                    isValid={!invalid && !!error}
                    errorMessage={error?.message}
                  />
                </ReactInputMask>
              )}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={onAddNewContact}
            disabled={!canAddNewContact}
            className={clsx({
              'opacity-50': !canAddNewContact,
            })}
          >
            <Add />
            Adicionar contato
          </Button>
        </fieldset>
      ) : (
        <>
          <fieldset className="mb-14">
            <legend className="mb-3 text-sm font-medium">Contatos</legend>

            <div className="mb-[28px] space-y-3">
              {contactFields.map(({ id }, index, array) => {
                const isSelected = currentContactIdBeingEdited === index
                const isFirstItem = currentContactIdBeingEdited === 0
                const lastArrayIndex = array.length - 1
                const isLastItem =
                  currentContactIdBeingEdited === lastArrayIndex
                const hasMoreOneItem = array.length > 1

                return (
                  <div key={id}>
                    <div
                      className={clsx('flex-1', {
                        'border-grey-700 border-dashed border-y py-3':
                          isSelected && hasMoreOneItem,
                        'pt-0 border-t-0': isFirstItem && hasMoreOneItem,
                        'pb-0 border-b-0': isLastItem && hasMoreOneItem,
                      })}
                    >
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="max-w-[80%] md:max-w-[85%] flex-1">
                          <Controller
                            name={`contacts.${index}.name`}
                            control={control}
                            render={({
                              field: { value = '', onChange },
                              fieldState: { invalid, error },
                            }) => (
                              <Input
                                isHighlighted={isSelected}
                                placeholder="Nome do contato"
                                value={value}
                                onChange={onChange}
                                disabled={!isSelected}
                                isValid={!invalid && !!error}
                                errorMessage={error?.message}
                                isDisabledAttrStyled={false}
                              />
                            )}
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentContactIdBeingEdited((prevState) => {
                                return prevState === index ? null : index
                              })
                            }}
                          >
                            <Edit
                              className={clsx('sm:w-[32px] sm:h-[32px]', {
                                'stroke-grey-900': isSelected,
                                'stroke-grey-700': !isSelected,
                              })}
                            />
                          </button>

                          <button
                            type="button"
                            onClick={() => onRemoveContact(index)}
                          >
                            <Trash className="sm:w-[32px] sm:h-[32px]" />
                          </button>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="space-y-3">
                          <div className="max-w-[80%] md:max-w-[85%]">
                            <Controller
                              name={`contacts.${index}.email`}
                              control={control}
                              render={({
                                field: { value = '', onChange },
                                fieldState: { invalid, error },
                              }) => (
                                <Input
                                  isHighlighted
                                  placeholder="E-mail"
                                  value={value}
                                  onChange={onChange}
                                  isValid={!invalid && !!error}
                                  errorMessage={error?.message}
                                />
                              )}
                            />
                          </div>

                          <div className="max-w-[80%] md:max-w-[85%]">
                            <Controller
                              name={`contacts.${index}.phone`}
                              control={control}
                              render={({
                                field: { value = '', onChange },
                                fieldState: { invalid, error },
                              }) => (
                                <ReactInputMask
                                  mask="(99) 99999-9999"
                                  maskPlaceholder={null}
                                  value={value}
                                  onChange={onChange}
                                >
                                  <Input
                                    isHighlighted
                                    placeholder="(00) 00000-0000"
                                    isValid={!invalid && !!error}
                                    errorMessage={error?.message}
                                  />
                                </ReactInputMask>
                              )}
                            />
                          </div>

                          <Button
                            type="submit"
                            size="small"
                            variant="outline"
                            onClick={() => handleSaveContact(index)}
                          >
                            Salvar
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <Button
              type="button"
              variant="outline"
              size="small"
              disabled={!canAddNewContact}
              className={clsx({
                'opacity-50': !canAddNewContact,
              })}
              onClick={onAddNewContact}
            >
              <Add />
              Adicionar contato
            </Button>
          </fieldset>
        </>
      )}

      <Button
        type="submit"
        isLoading={isSubmitting}
        disabled={!formState.isValid}
        fullWidth
      >
        {defaultValues ? 'Salvar' : 'Cadastrar'}
      </Button>
    </form>
  )
}
